import { axiosPrivate } from "@/lib/axios/axios-private";
import { apiErrorFromEnvelope } from "@/lib/api/errors";
import type {
  ListVideoScreenshotsParams,
  VideoScreenshotItem,
  VideoScreenshotListData,
  VideoScreenshotListEnvelope,
  VideoScreenshotListResult,
} from "@/types/video-screenshot-types";

function parseScreenshotPayload(
  payload: VideoScreenshotListData | undefined,
  fallbackPage: number,
  fallbackPageSize: number,
): VideoScreenshotListResult {
  if (!payload) {
    return {
      items: [],
      page: fallbackPage,
      pageSize: fallbackPageSize,
      total: 0,
      video: null,
    };
  }

  return {
    items: (payload.items ?? []).map((item) => {
      const raw = item as VideoScreenshotItem & {
        confidence_percentage?: number | string | null;
      };
      return {
        ...item,
        confidencePercentage:
          raw.confidencePercentage ?? raw.confidence_percentage,
      };
    }),
    page: payload.page ?? fallbackPage,
    pageSize: payload.pageSize ?? fallbackPageSize,
    total: payload.total ?? 0,
    video: payload.video ?? null,
  };
}

export async function fetchVideoScreenshots(
  videoId: string,
  params: ListVideoScreenshotsParams = {},
): Promise<VideoScreenshotListResult> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const trimmedId = videoId.trim();

  if (!trimmedId) {
    throw apiErrorFromEnvelope(
      {
        success: false,
        code: 400,
        errorMessage: "A video ID is required to load screenshots.",
      },
      "screenshots",
    );
  }

  const { data: envelope } = await axiosPrivate.get<VideoScreenshotListEnvelope>(
    `/api/history/${encodeURIComponent(trimmedId)}/screenshots`,
    {
      params: {
        page,
        pageSize,
        sortBy: params.sortBy ?? "createdAt",
        sortOrder: params.sortOrder ?? "desc",
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        ...(params.screenshotId?.trim()
          ? { screenshotId: params.screenshotId.trim() }
          : {}),
      },
    },
  );

  if (!envelope.success || envelope.code !== 200) {
    throw apiErrorFromEnvelope(envelope, "screenshots");
  }

  return parseScreenshotPayload(envelope.data, page, pageSize);
}

export async function findVideoScreenshotById(
  videoId: string,
  screenshotId: string,
): Promise<VideoScreenshotItem | null> {
  const targetId = screenshotId.trim();
  if (!targetId) return null;

  const direct = await fetchVideoScreenshots(videoId, {
    screenshotId: targetId,
    page: 1,
    pageSize: 1,
  });
  const fromDirect = direct.items.find((item) => item.id === targetId);
  if (fromDirect) return fromDirect;

  let page = 1;
  const pageSize = 50;
  let totalPages = 1;

  while (page <= totalPages) {
    const result = await fetchVideoScreenshots(videoId, {
      page,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "desc",
    });
    const found = result.items.find((item) => item.id === targetId);
    if (found) return found;

    totalPages = getVideoScreenshotsTotalPages(result);
    if (result.items.length === 0) break;
    page += 1;
  }

  return null;
}

export function getVideoScreenshotsTotalPages(
  result: VideoScreenshotListResult,
): number {
  if (result.total > 0 && result.pageSize > 0) {
    return Math.max(1, Math.ceil(result.total / result.pageSize));
  }

  return Math.max(1, result.page);
}

export type { VideoScreenshotItem } from "@/types/video-screenshot-types";
